// Sidebar navigation config — the single source of truth for which modules
// exist, their routes / sub-menus / icons, and which users see them. Used by
// the desktop sidebar (AppLayout) and the mobile shell (src/mobile/).
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, Workflow, ClipboardList, ShieldCheck, FlaskConical, Factory, ShoppingCart, Truck, Users as UsersIcon, FileText, ReceiptText, Gauge, PackageOpen, PackageCheck, PackageSearch, UserCog, Briefcase, Settings, GitBranch, ScrollText, ClipboardCheck, FilePlus, Home, MapPin, Receipt, ListTodo, BarChart3, User, ShoppingBag, FileCheck2, CalendarClock, Table2, Ruler, Wrench, AlertTriangle, ClipboardSignature, RotateCcw, ListChecks, FileBarChart, Boxes, Building2, Handshake, PenTool, Database, Target, IdCard,
} from "lucide-react";

// Role → allowed nav paths. `admin` sees everything. Anything not in the map
// falls through to legacy behaviour (visible to all logged-in users).
export const ROLE_MENU = {
  production_manager: new Set(["/", "/design", "/routing", "/capacity", "/mrp", "/auto-procurement", "/stock-availability", "/work-orders", "/production-flow", "/production-approvals", "/quality/incoming-inspection", "/quality/production-ncrs", "/quality/rework-orders", "/indents", "/reports", "/supplier-master"]),
  quality_inspector:  new Set(["/", "/design", "/quality", "/quality-inspections", "/quality/incoming-inspection", "/quality/calibration", "/quality/production-ncrs", "/quality/rework-orders", "/goods-inward", "/goods-receipt", "/supplier-master"]),
  // Inventory now lives inside Production Flow (see PRODUCTION_FLOW_NAV
  // below) — store_manager gets the whole module so its "Inventory" item
  // is reachable, same as before the merge.
  store_manager:      new Set(["/", "/design", "/production-flow", "/stock-ledger", "/stock-availability", "/purchase-requests", "/goods-inward", "/goods-receipt", "/indents", "/reports", "/supplier-master"]),
  sales_crm:          new Set(["/", "/design", "/customers", "/dealer-master", "/crm", "/quotations", "/raise-transfer-order", "/sales-orders", "/stock-availability", "/order-status", "/order-status-sheet", "/customer-sales-orders", "/proforma-invoices", "/packing-slips", "/delivery-challans", "/tax-invoices", "/reports"]),
  marketing:          new Set(["/", "/design", "/customers", "/dealer-master", "/quotations", "/raise-transfer-order", "/sales-orders", "/stock-availability", "/order-status", "/order-status-sheet", "/customer-sales-orders", "/proforma-invoices"]),
  // Supplier / Dealer Management functional roles (backend/partners/config.py)
  purchase_manager:   new Set(["/", "/design", "/supplier-master", "/suppliers", "/mrp", "/stock-availability", "/purchase-requests", "/purchase-orders", "/auto-procurement", "/goods-inward", "/goods-receipt", "/indents", "/reports"]),
  purchase_executive: new Set(["/", "/design", "/supplier-master", "/suppliers", "/stock-availability", "/purchase-requests", "/purchase-orders", "/auto-procurement", "/goods-inward", "/goods-receipt", "/indents"]),
  sales_manager:      new Set(["/", "/design", "/dealer-master", "/customers", "/crm", "/quotations", "/raise-transfer-order", "/sales-orders", "/stock-availability", "/order-status", "/order-status-sheet", "/customer-sales-orders", "/proforma-invoices", "/packing-slips", "/delivery-challans", "/tax-invoices", "/reports"]),
  sales_executive:    new Set(["/", "/design", "/dealer-master", "/customers", "/crm", "/quotations", "/raise-transfer-order", "/sales-orders", "/stock-availability", "/order-status", "/customer-sales-orders", "/proforma-invoices"]),
  accounts:           new Set(["/", "/supplier-master", "/dealer-master", "/purchase-orders", "/goods-receipt", "/tax-invoices", "/delivery-challans"]),
  finance_manager:    new Set(["/", "/supplier-master", "/dealer-master", "/purchase-orders", "/goods-receipt", "/tax-invoices", "/delivery-challans", "/reports"]),
  // Design & Engineering roles (backend/design_engineering/config.py).
  design_engineer:    new Set(["/", "/design", "/items", "/routing", "/stock-availability"]),
  design_checker:     new Set(["/", "/design", "/items", "/routing", "/stock-availability"]),
  design_head:        new Set(["/", "/design", "/items", "/routing", "/stock-availability", "/reports"]),
  management:         new Set(["/", "/design", "/supplier-master", "/dealer-master", "/mrp", "/stock-availability", "/purchase-orders", "/auto-procurement", "/sales-orders", "/customer-sales-orders", "/order-status", "/tax-invoices", "/reports"]),
  // HR manages user accounts (Users & Roles, shown in the Administration
  // section below). read_only has no entry: it sees every module, and the
  // backend refuses any change it tries to make.
  hr:                 new Set(["/", "/users"]),
};

// Module key (Phase 20 permissions matrix) → nav path.  Must match backend MODULE_CATALOG.
export const MODULE_TO_PATH = {
  dashboard: "/",
  items_bom: "/items",
  routing: "/routing",
  capacity: "/capacity",
  mrp: "/mrp",
  work_orders: "/work-orders",
  production_flow: "/production-flow",
  production_approvals: "/production-approvals",
  quality: "/quality",
  qc_inspections: "/quality-inspections",
  incoming_inspection: "/quality/incoming-inspection",
  calibration: "/quality/calibration",
  production_ncrs: "/quality/production-ncrs",
  production_rework_orders: "/quality/rework-orders",
  // Inventory now lives inside Production Flow — a user with just the
  // "inventory" module permission gets the whole module so their
  // "Inventory" item under it is reachable, same as before the merge.
  inventory: "/production-flow",
  indents: "/indents",
  stock_ledger: "/stock-ledger",
  stock_availability: "/stock-availability",
  purchase_requests: "/purchase-requests",
  purchase_orders: "/purchase-orders",
  auto_procurement: "/auto-procurement",
  suppliers: "/suppliers",
  supplier_master: "/supplier-master",
  goods_inward: "/goods-inward",
  goods_receipt: "/goods-receipt",
  customers: "/customers",
  dealer_master: "/dealer-master",
  crm: "/crm",
  quotations: "/quotations",
  quotations_new: "/raise-quotation",
  raise_transfer_order: "/raise-transfer-order",
  transfer_orders: "/sales-orders",
  order_status: "/order-status",
  order_status_sheet: "/order-status-sheet",
  customer_sales_orders: "/customer-sales-orders",
  proforma_invoices: "/proforma-invoices",
  packing_slips: "/packing-slips",
  delivery_challans: "/delivery-challans",
  tax_invoices: "/tax-invoices",
  // Reports group — every report lives under /reports/*, so granting any
  // report module makes the "Reports" item (and its submenu) reachable.
  dc_vs_gin_report: "/reports",
  pending_qc_report: "/reports",
  pending_so_report: "/reports",
  pending_production_report: "/reports",
  // Sales Report sits outside NAV (its own sidebar section below), so it is
  // gated by `canSeeSalesReport` rather than this path allow-list.
  sales_report: "/sales-report",
  design_engineering: "/design",
  // Tally Data sits in the Administration section; a non-admin granted this
  // module sees just that one item there (read-only — settings stay admin).
  tally_data: "/tally",
  // HR (HR app + Logbook) — gated by `canSeeHr` like Sales Report: admins,
  // role-default users, or the "hr" module ticked on User Permissions.
  hr: "/hr",
};
// OTD Command Board is the daily production meeting board — every department
// takes part, so it is shown to every user regardless of role/module
// permissions (the backend decides what each user may change on it).
export const ALWAYS_VISIBLE_PATHS = new Set(["/otd-board"]);
// Admin-only paths never appear in the module catalog — always kept for admin.
export const ADMIN_ONLY_PATHS = new Set(["/users", "/user-permissions", "/settings", "/tally"]);

export const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "nav-dashboard" },
  { to: "/hr", label: "HR", icon: IdCard, tid: "nav-hr" },
  { to: "/otd-board", label: "OTD Command Board", icon: Target, tid: "nav-otd-board" },
  { to: "/items", label: "Items & BOM", icon: Package, tid: "nav-items" },
  { to: "/design", label: "Design & Engineering", icon: PenTool, tid: "nav-design" },
  { to: "/routing", label: "Routing", icon: Workflow, tid: "nav-routing" },
  { to: "/capacity", label: "Capacity & Load", icon: Gauge, tid: "nav-capacity" },
  { to: "/mrp", label: "MRP Run", icon: FlaskConical, tid: "nav-mrp" },
  { to: "/work-orders", label: "Work Orders", icon: ClipboardList, tid: "nav-work-orders" },
  { to: "/production-flow", label: "Production Flow", icon: GitBranch, tid: "nav-production-flow" },
  { to: "/production-approvals", label: "Production Approvals", icon: ClipboardCheck, tid: "nav-production-approvals" },
  { to: "/quality", label: "Quality", icon: ShieldCheck, tid: "nav-quality" },
  { to: "/quality-inspections", label: "QC Inspections", icon: ClipboardList, tid: "nav-quality-inspections" },
  { to: "/quality/incoming-inspection", label: "Incoming/In-Process Inspection", icon: Ruler, tid: "nav-incoming-inspection" },
  { to: "/quality/calibration", label: "Calibration", icon: Wrench, tid: "nav-calibration" },
  { to: "/quality/production-ncrs", label: "Production NCRs", icon: AlertTriangle, tid: "nav-production-ncrs" },
  { to: "/quality/rework-orders", label: "Rework Orders", icon: RotateCcw, tid: "nav-rework-orders" },
  { to: "/indents", label: "Indents", icon: ClipboardSignature, tid: "nav-indents" },
  { to: "/stock-ledger", label: "Stock Ledger", icon: ScrollText, tid: "nav-stock-ledger" },
  { to: "/stock-availability", label: "Stock Availability", icon: Boxes, tid: "nav-stock-availability" },
  { to: "/purchase-requests", label: "Purchase Requests", icon: ShoppingCart, tid: "nav-purchase-requests" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ReceiptText, tid: "nav-purchase-orders" },
  { to: "/auto-procurement", label: "Auto Procurement (MRP)", icon: CalendarClock, tid: "nav-auto-procurement" },
  { to: "/supplier-master", label: "Supplier Management", icon: Building2, tid: "nav-supplier-master" },
  { to: "/suppliers", label: "Suppliers", icon: Factory, tid: "nav-suppliers" },
  { to: "/goods-inward", label: "Goods Inward (GIN)", icon: PackageOpen, tid: "nav-goods-inward" },
  { to: "/goods-receipt", label: "Goods Receipt (GRN)", icon: PackageCheck, tid: "nav-goods-receipt" },
  { to: "/dealer-master", label: "Dealer Management", icon: Handshake, tid: "nav-dealer-master" },
  { to: "/customers", label: "Customers", icon: UsersIcon, tid: "nav-customers" },
  { to: "/crm", label: "CRM · Leads", icon: Briefcase, tid: "nav-crm" },
  { to: "/quotations", label: "Quotations", icon: FileText, tid: "nav-quotations" },
  { to: "/raise-transfer-order", label: "Raise Transfer Order", icon: FilePlus, tid: "nav-raise-transfer-order" },
  { to: "/sales-orders", label: "Transfer Orders", icon: FileText, tid: "nav-sales-orders" },
  { to: "/order-status-sheet", label: "Order Status Sheet", icon: Table2, tid: "nav-order-status-sheet" },
  { to: "/customer-sales-orders", label: "Sales Order", icon: ShoppingBag, tid: "nav-customer-sales-orders" },
  { to: "/proforma-invoices", label: "Proforma Invoices", icon: Receipt, tid: "nav-proforma-invoices" },
  { to: "/packing-slips", label: "Packing Slips", icon: PackageSearch, tid: "nav-packing-slips" },
  { to: "/delivery-challans", label: "Delivery Challans", icon: Truck, tid: "nav-delivery-challans" },
  { to: "/tax-invoices", label: "Tax Invoices", icon: FileCheck2, tid: "nav-tax-invoices" },
  { to: "/reports", label: "Reports", icon: FileBarChart, tid: "nav-reports" },
];

export const ADMIN_NAV = [
  { to: "/users", label: "Users & Roles", icon: UserCog, tid: "nav-users" },
  { to: "/user-permissions", label: "User Permissions", icon: ShieldCheck, tid: "nav-user-permissions" },
  { to: "/tally", label: "Tally Data", icon: Database, tid: "nav-tally" },
  { to: "/settings", label: "Settings", icon: Settings, tid: "nav-settings" },
];

// Production Flow submenu — each stage opens a dedicated single-stage page
// (Issue / Receive / Create Job Work DC actions live there). Order follows
// the actual process flow; Finished Goods is last and has no DC action.
// Raw Material isn't a job stage (no job exists yet) — it lists RM Store
// stock instead, with Issue + Create Job Work DC only, no Receive.
// Inventory (first) replaces the old standalone Inventory module — every
// store's stock in one table (RM Store through Finished Goods), not just
// the 9 production stages. The multi-column "All Stages" Kanban overview
// (still reachable at /production-flow directly) was dropped from this
// submenu by request — operators work stage-by-stage, not from an overview.
export const PRODUCTION_FLOW_NAV = [
  { to: "/production-flow/inventory", label: "Inventory", tid: "nav-production-flow-inventory" },
  { to: "/production-flow/raw_material", label: "Raw Material", tid: "nav-production-flow-raw_material" },
  { to: "/production-flow/machine_shop", label: "Machine Shop", tid: "nav-production-flow-machine_shop" },
  { to: "/production-flow/impregnation", label: "Impregnation", tid: "nav-production-flow-impregnation" },
  { to: "/production-flow/fabrication", label: "Fabrication", tid: "nav-production-flow-fabrication" },
  { to: "/production-flow/powder_coating", label: "Powder Coating", tid: "nav-production-flow-powder_coating" },
  { to: "/production-flow/semi_finished", label: "Semi Finished", tid: "nav-production-flow-semi_finished" },
  { to: "/production-flow/assembly", label: "Assembly", tid: "nav-production-flow-assembly" },
  { to: "/production-flow/testing", label: "Testing", tid: "nav-production-flow-testing" },
  { to: "/production-flow/final_inspection", label: "Final Inspection", tid: "nav-production-flow-final_inspection" },
  { to: "/production-flow/finished", label: "Finished Goods", tid: "nav-production-flow-finished" },
];

// Incoming/In-Process Inspection submenu — merged in from the standalone
// SPC/NCR tool. New Inspection is the "/quality/incoming-inspection" toggle
// item itself; these three links only need to reach its subroutes.
export const INCOMING_INSPECTION_NAV = [
  { to: "/quality/incoming-inspection", label: "New Inspection", tid: "nav-incoming-inspection-new" },
  { to: "/quality/incoming-inspection/history", label: "Report History", tid: "nav-incoming-inspection-history" },
  { to: "/quality/incoming-inspection/ncr-register", label: "NCR Register", tid: "nav-incoming-inspection-ncr-register" },
];

// Design & Engineering submenu (PLM-lite module — backend/design_engineering/).
// Settings is filtered to admin / design_head in the render below; every
// other page checks the user's design permissions itself.
export const DESIGN_NAV = [
  { to: "/design", label: "Dashboard", tid: "nav-design-dashboard" },
  { to: "/design/approvals", label: "My Queue", tid: "nav-design-approvals" },
  { to: "/design/requests", label: "Design Requests", tid: "nav-design-requests" },
  { to: "/design/items", label: "Item Master", tid: "nav-design-items" },
  { to: "/design/documents", label: "Drawings & Documents", tid: "nav-design-documents" },
  { to: "/design/boms", label: "Bills of Material", tid: "nav-design-boms" },
  { to: "/design/routings", label: "Routings", tid: "nav-design-routings" },
  { to: "/design/changes", label: "ECR / ECN", tid: "nav-design-changes" },
  { to: "/design/standards", label: "Standards Library", tid: "nav-design-standards" },
  { to: "/design/npd", label: "NPD Projects", tid: "nav-design-npd" },
  { to: "/design/reports", label: "Reports & Audit", tid: "nav-design-reports" },
  { to: "/design/settings", label: "Settings", tid: "nav-design-settings", roles: ["admin", "design_head"] },
];

// Tabs across the top of the HR pages (pages/hr/HrTabs.jsx) — the Quickair
// HR app, plus Logbook (pages/hr/Logbook.jsx, backend/hr_logbook.py): every
// Logbook screen except Time Tracking. The sidebar shows HR as one item.
export const HR_NAV = [
  { to: "/hr", label: "HR App", tid: "nav-hr-app" },
  { to: "/hr/logbook/mine", label: "My Tasks", tid: "nav-hr-logbook-mine" },
  { to: "/hr/logbook/given", label: "Assigned by Me", tid: "nav-hr-logbook-given" },
  { to: "/hr/logbook/team", label: "Team Board", tid: "nav-hr-logbook-team" },
  { to: "/hr/logbook/assign", label: "Assign Task", tid: "nav-hr-logbook-assign" },
  { to: "/hr/logbook/admin", label: "Logbook Admin", tid: "nav-hr-logbook-admin" },
];

// Reports submenu — cross-module, read-only reports.
export const REPORTS_NAV = [
  { to: "/reports/dc-vs-gin", label: "DC vs GIN Report", tid: "nav-reports-dc-vs-gin" },
  { to: "/reports/pending-qc", label: "Pending QC Report", tid: "nav-reports-pending-qc" },
  { to: "/reports/pending-sales-orders", label: "Pending Sales Order Report", tid: "nav-reports-pending-so" },
  { to: "/reports/pending-production", label: "Pending Production Orders", tid: "nav-reports-pending-production" },
];

// CRM submenu — "All Leads" is the existing manual/mixed-source pipeline
// (still at /crm itself); "IndiaMART Leads" is the new Pull-API-fed list.
export const CRM_NAV = [
  { to: "/crm", label: "All Leads", tid: "nav-crm-all" },
  { to: "/crm/indiamart", label: "IndiaMART Leads", tid: "nav-crm-indiamart" },
];

// Sales Report module (Phase 6 — sidebar wiring only, no new backend logic).
// Shown to admins and to users without explicit module_permissions (role
// default). A user with explicit module_permissions sees it only when the
// "sales_report" module is ticked on User Permissions. A user who hasn't
// opted into the module yet still gets a friendly message on Home (see
// SalesHome.jsx).
export const SALES_REPORT_NAV = [
  { to: "/sales-report/home", label: "Home", icon: Home, tid: "nav-sales-report-home" },
  { to: "/sales-report/visits", label: "Visits", icon: MapPin, tid: "nav-sales-report-visits" },
  { to: "/sales-report/expenses", label: "Expenses", icon: Receipt, tid: "nav-sales-report-expenses" },
  { to: "/sales-report/plans", label: "Plans", icon: ListTodo, tid: "nav-sales-report-plans" },
  { to: "/sales-report/tasks", label: "Tasks", icon: ListChecks, tid: "nav-sales-report-tasks" },
  { to: "/sales-report/reports", label: "Reports", icon: BarChart3, tid: "nav-sales-report-reports" },
  { to: "/sales-report/profile", label: "Profile", icon: User, tid: "nav-sales-report-profile" },
];

// Which nav entries `user` sees, exactly as the desktop sidebar has always
// computed it, and bounces the user to Dashboard from a URL outside their
// allow-list. Returns the pieces both layouts render from.
export function useVisibleNav(user) {
  const nav = useNavigate();
  const location = useLocation();

  // Priority order:
  //  1. Admin → sees everything
  //  2. Explicit per-user module_permissions (Phase 20) → filter NAV to only those paths
  //  3. Legacy role-menu allow-list (backward compat)
  //  4. No restriction (legacy fine-grained roles)
  const perms = user?.module_permissions || [];
  let allowedPaths = null;
  if (user?.role === "admin") {
    allowedPaths = null;                                     // full access
  } else if (perms.length > 0) {
    const paths = new Set(perms.map((k) => MODULE_TO_PATH[k]).filter(Boolean));
    if (perms.includes("quotations")) paths.add("/raise-quotation");
    if (!paths.has("/")) paths.add("/");                     // Dashboard always reachable
    if (user?.role === "hr") paths.add("/users");            // HR's own screen
    allowedPaths = paths;
  } else if (ROLE_MENU[user?.role]) {
    allowedPaths = ROLE_MENU[user.role];
  }

  // Sales Report: admin and role-default users (no explicit module
  // permissions) keep seeing it; explicit permissions must include it.
  const canSeeSalesReport = user?.role === "admin" || perms.length === 0 || perms.includes("sales_report");
  // HR: same rule, with the "hr" module (backend: hr_bridge.can_use_hr).
  const canSeeHr = user?.role === "admin" || perms.length === 0 || perms.includes("hr");

  const visibleAdminNav = user?.role === "admin"
    ? ADMIN_NAV
    : ADMIN_NAV.filter((n) => (n.to === "/tally" && perms.includes("tally_data")) || (n.to === "/users" && user?.role === "hr"));

  // HR follows `canSeeHr` rather than the path allow-list (role-scoped
  // users without explicit permissions keep it, as before).
  const visibleNav = NAV.filter((n) => (n.to === "/hr" ? canSeeHr : !allowedPaths
    || allowedPaths.has(n.to) || ALWAYS_VISIBLE_PATHS.has(n.to) || (user?.role === "admin" && ADMIN_ONLY_PATHS.has(n.to))));

  // If a scoped user hits a URL not in their allow-list, bounce to Dashboard.
  // Sales Report is gated by `canSeeSalesReport` instead of the path
  // allow-list (role-scoped users without explicit permissions keep it);
  // access within the module (opted-in or not) is handled by its own
  // pages/backend.
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/sales-report")) {
      if (!canSeeSalesReport) nav("/", { replace: true });
      return;
    }
    if (path === "/hr" || path.startsWith("/hr/")) {
      if (!canSeeHr) nav("/", { replace: true });
      return;
    }
    if (!allowedPaths) return;
    // Daily Department Reports — same exemption as Sales Report: every role
    // has its own department report, gated inside the page/backend rather
    // than by the sidebar module-permission allow-list.
    if (path.startsWith("/daily-reports")) return;
    if ([...ALWAYS_VISIBLE_PATHS].some((p) => path === p || path.startsWith(p + "/"))) return;
    // Match by prefix so nested routes still work if we add them later
    const allowed = [...allowedPaths].some((p) => p === "/" ? path === "/" : path === p || path.startsWith(p + "/"));
    if (!allowed) nav("/", { replace: true });
  }, [location.pathname, allowedPaths, canSeeSalesReport, canSeeHr, nav]);

  return { perms, allowedPaths, canSeeSalesReport, canSeeHr, visibleAdminNav, visibleNav };
}
